import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Link } from 'react-router-dom';
import { ShoppingCart, Eye, Star, TrendingUp } from 'lucide-react';

interface Product {
  id: string;
  name: string;
  description?: string;
  sku: string;
  ean?: string;
  regularPrice: number;
  salePrice: number;
  mainImage?: string;
  category: string | null;
  isPrivateSale: boolean;
  companyId: string;
  promotionalPeriod?: {
    startDate: string;
    endDate: string;
  };
  isProPlus?: boolean;
  proPlusPrice?: number;
  status: string;
  stock: number;
  companyName?: string;
}

const isPromotionalPriceActive = (product: Product): boolean => {
  if (!product.promotionalPeriod?.startDate || !product.promotionalPeriod?.endDate) {
    return false;
  }

  const now = new Date().getTime();
  const startDate = new Date(product.promotionalPeriod.startDate).getTime();
  const endDate = new Date(product.promotionalPeriod.endDate).getTime();

  return now >= startDate && now <= endDate;
};

const ProductCard: React.FC<{ product: Product }> = ({ product }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group">
      {/* Imagem do Produto */}
      <div className="relative aspect-square overflow-hidden cursor-pointer">
        <img
          src={product.mainImage}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
          <Eye size={24} className="text-white opacity-0 group-hover:opacity-100 transform scale-50 group-hover:scale-100 transition-all duration-300" />
        </div>
        
        {/* Badge de Stock */}
        <div className="absolute top-3 left-3">
          <span className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-medium text-gray-700">
            Stock: {product.stock}
          </span>
        </div>
      </div>

      {/* Informações do Produto */}
      <div className="p-4">
        {/* Nome do Produto */}
        <h3 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2 min-h-[2.5rem]">
          {product.name}
        </h3>

        {/* Preços */}
        <div className="space-y-2 mb-4">
          {/* Preço de Revenda */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Revenda:</span>
            <span className="text-lg font-bold text-[#0a192f]">
              {(product.proPlusPrice || product.regularPrice).toFixed(2)} €
            </span>
          </div>
          
          {/* Preço PVPR */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">PVPR:</span>
            <span className="text-sm text-gray-900">
              {isPromotionalPriceActive(product) ? product.salePrice.toFixed(2) : product.regularPrice.toFixed(2)} €
            </span>
          </div>
          
          {/* Lucro potencial */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Lucro:</span>
            <span className="text-sm font-bold text-[#8cc63f]">
              {(
                (isPromotionalPriceActive(product) ? product.salePrice : product.regularPrice) - 
                (product.proPlusPrice || product.regularPrice)
              ).toFixed(2)} €
            </span>
          </div>
        </div>
        
        {/* Botão de ação */}
        <Link to="/register" className="w-full bg-[#8cc63f] hover:bg-[#7ab52f] text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2">
          <ShoppingCart size={16} />
          Registar para comprar
        </Link>
      </div>
    </div>
  );
};

const LandingPageProducts: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        
        // Obter produtos ordenados por data de criação (mais recentes primeiro)
        const productsRef = collection(db, 'products');
        const productsQuery = query(
          productsRef,
          orderBy('createdAt', 'desc')
        );
        
        const productsSnapshot = await getDocs(productsQuery);
        console.log('Total de produtos encontrados:', productsSnapshot.size);
        
        // Filtrar produtos em memória usando a mesma lógica da área drop
        const filteredDocs = productsSnapshot.docs.filter(doc => {
          const data = doc.data();
          const isApproved = data.status === 'approved';
          const hasStock = (data.stock || 0) > 0;
          const hasBaselinkerID = !!data.baselinker_id;
          const isProPlus = data.isProPlus === true;
          const hasProPlusPrice = !!data.proPlusPrice && data.proPlusPrice > 0;
          
          return isApproved && hasStock && hasBaselinkerID && isProPlus && hasProPlusPrice;
        });
        
        console.log('Produtos filtrados:', filteredDocs.length);
        
        // Processar os produtos filtrados (limitando a 8)
        const productsPromises = filteredDocs.slice(0, 8).map(async (doc) => {
          const data = doc.data();
          
          const productData: Product = {
            id: doc.id,
            name: data.name || '',
            sku: data.sku || '',
            regularPrice: Number(data.regularPrice) || 0,
            salePrice: Number(data.salePrice) || 0,
            companyId: data.companyId || '',
            isPrivateSale: true,
            isProPlus: Boolean(data.isProPlus),
            proPlusPrice: Number(data.proPlusPrice) || 0,
            description: data.description,
            ean: data.ean,
            mainImage: data.mainImage || data.baselinkerMainImage,
            category: data.category || null,
            promotionalPeriod: data.promotionalPeriod,
            status: data.status,
            stock: data.stock,
            companyName: 'Anônimo' // Nome do vendedor anônimo
          };
          
          return productData;
        });
        
        const productsData = await Promise.all(productsPromises);
        console.log('Produtos processados para exibição:', productsData.length);
        
        setProducts(productsData);
      } catch (error) {
        console.error('Erro ao carregar produtos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  if (loading) {
    return (
      <div className="py-12 text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#8cc63f] border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
        <p className="mt-4 text-white">A carregar produtos...</p>
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="text-center mb-10">
        <div className="inline-block mb-6 px-4 py-1 bg-[rgba(140,198,63,0.2)] rounded-full">
          <p className="text-[#8cc63f] font-semibold">PRODUTOS EM DESTAQUE</p>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
          Produtos prontos para vender na sua loja
        </h2>
        <p className="text-xl max-w-4xl mx-auto mb-8 text-white/80">
          Aceda a mais de 200.000 produtos com margens de lucro atrativas e comece a vender hoje mesmo
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {products.map(product => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>

      <div className="mt-12 text-center">
        <Link to="/register" className="inline-block px-8 py-4 rounded-lg bg-[#8cc63f] text-white hover:bg-[#7ab52f] transition-colors text-xl font-bold transform hover:scale-105 transition-transform shadow-lg">
          REGISTAR PARA VER MAIS PRODUTOS
        </Link>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
          <div className="flex items-center mb-4">
            <div className="bg-[#8cc63f] rounded-full p-2 mr-3">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white">Margens Atrativas</h3>
          </div>
          <p className="text-white/80">Produtos com margens de lucro que podem chegar a 40% do preço de venda ao público.</p>
        </div>

        <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
          <div className="flex items-center mb-4">
            <div className="bg-[#8cc63f] rounded-full p-2 mr-3">
              <Star className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white">Produtos Premium</h3>
          </div>
          <p className="text-white/80">Acesso a produtos de alta qualidade e marcas reconhecidas para oferecer aos seus clientes.</p>
        </div>

        <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
          <div className="flex items-center mb-4">
            <div className="bg-[#8cc63f] rounded-full p-2 mr-3">
              <ShoppingCart className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white">Sem Stock Inicial</h3>
          </div>
          <p className="text-white/80">Venda sem investimento inicial em stock. Só paga pelos produtos que vender.</p>
        </div>
      </div>
    </div>
  );
};

export default LandingPageProducts;
